"use client";

import React from "react";
import { Info as HelpCircle, ShieldCheck, ArrowRight } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
const faqs = [{
  id: "item-1",
  question: "Do BMF Scaffolding cover Basingstoke?",
  answer: "Yes. Basingstoke and the surrounding Hampshire area are part of our core coverage, alongside Berkshire, Surrey, Buckinghamshire, London, and the wider South East."
}, {
  id: "item-2",
  question: "What types of scaffolding can you provide in Basingstoke?",
  answer: "We erect scaffolding for residential, commercial, industrial, and public sector sites. This includes independent access scaffolds, temporary roofs, edge protection, chimney scaffolds, new build support, and specialist access systems."
}, {
  id: "item-3",
  question: "Can you provide scaffolding for roof work?",
  answer: "Yes. We regularly supply access towers, perimeter handrails, edge protection, chimney stacks, and temporary roof structures for domestic re-roofing, solar installations, and commercial roof replacements."
}, {
  id: "item-4",
  question: "Do you work with contractors and developers?",
  answer: "Yes. We partner directly with main contractors, developers, roofing contractors, solar installers, local councils, and facilities management companies. We operate to strict safety standards with NASC and CHAS accreditations."
}, {
  id: "item-5",
  question: "Can scaffolding be installed where access is restricted?",
  answer: "Yes. Our team handles complex footprints, narrow side passages, public footpaths requiring licensing advice, internal courtyards, and specialist cantilevered or bridged access requirements."
}, {
  id: "item-6",
  question: "How do I request a quote in Basingstoke?",
  answer: "You can submit your project details through our online quote form or get in touch directly. We review drawings, site photos, or arrange a site visit to provide a clear, accurate, and itemised quotation."
}];
export default function BasingstokeFaq() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="basingstokefaq" data-nav="light" className="relative bg-background py-16 sm:py-20 md:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
          {/* Left Column: Context & Contact */}
          <motion.div initial={shouldReduceMotion ? false : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="lg:col-span-5 flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Badge variant="outline" className="border-border text-foreground px-2.5 py-1 text-xs">
                  <HelpCircle className="size-3.5 text-primary mr-1" />
                  Basingstoke & Hampshire
                </Badge>
              </div>

              <h2 className="font-default text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
                Frequently asked questions
              </h2>

              <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                Key details regarding our scaffolding services, safety accreditations, and site coverage across Basingstoke and nearby towns.
              </p>

              <div className="mt-8 rounded-lg border border-border bg-card p-6 shadow-xs">
                <div className="flex items-start gap-3">
                  <div className="rounded-md bg-muted p-2 text-primary shrink-0">
                    <ShieldCheck className="size-5" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-card-foreground">
                      Accredited scaffold contractors
                    </h3>
                    <p className="mt-1 text-xs text-muted-foreground leading-normal">
                      Full compliance with NASC and CHAS standards on all Basingstoke residential and commercial contracts.
                    </p>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-border flex flex-col sm:flex-row gap-3">
                  <Button asChild className="w-full sm:w-auto" size="sm">
                    <Link to="/ContactRequestQuotePage">
                      Request a quote
                      <ArrowRight className="size-4 ml-1.5" />
                    </Link>
                  </Button>
                  <Button asChild variant="outline" size="sm" className="w-full sm:w-auto">
                    <Link to="/AreasWeCoverPage">
                      View all areas
                    </Link>
                  </Button>
                </div>
              </div>
            </div>

            <div className="mt-8 text-xs text-muted-foreground">
              Need technical advice on site access or scaffolding licenses?{" "}
              <Link to="/ContactRequestQuotePage" className="text-primary hover:underline font-medium">
                Speak with our team
              </Link>
            </div>
          </motion.div>

          {/* Right Column: Accordion */}
          <motion.div initial={shouldReduceMotion ? false : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1,
          ease: "easeOut"
        }} className="lg:col-span-7">
            <Accordion type="single" collapsible defaultValue="item-1" className="w-full space-y-3">
              {faqs.map((faq, index) => <div key={faq.id} data-index={index}>
                  <AccordionItem value={faq.id} className="rounded-lg border border-border bg-card px-5 py-1 shadow-2xs transition-[border-color,box-shadow] duration-200 hover:border-primary/40">
                    <AccordionTrigger className="text-left font-default text-base font-semibold text-card-foreground py-4 hover:no-underline">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm leading-relaxed text-muted-foreground pb-4">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                </div>)}
            </Accordion>
          </motion.div>
        </div>
      </div>
    </section>;
}